import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getAllAssetsByCaseId,
    getAssetById,
    createAsset,
    updateAsset,
    deleteAsset,
    searchAssets,
    formatAssetData,
    parseAttachmentIds
} from "../../../api/caseAssetOtherDetails_api";

// =========== GET ALL ASSETS BY CASE ID ===========
export const getAllAssetsByCaseIdAction = createAsyncThunk(
    "otherDetails/getAllAssetsByCaseId",
    async (caseDetailsID, { rejectWithValue }) => {
        try {
            const result = await getAllAssetsByCaseId(caseDetailsID);

            if (result.success) {
                return {
                    caseDetailsID,
                    assets: result.data,
                    parties: result.parties,
                    count: result.count,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch assets",
                error: error.message
            });
        }
    }
);

// =========== GET SINGLE ASSET BY ID ===========
export const getAssetByIdAction = createAsyncThunk(
    "otherDetails/getAssetById",
    async (id, { rejectWithValue }) => {
        try {
            const result = await getAssetById(id);

            if (result.success) {
                return {
                    asset: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch asset",
                error: error.message
            });
        }
    }
);

// =========== CREATE ASSET ===========
export const createAssetAction = createAsyncThunk(
    "otherDetails/createAsset",
    async ({ caseDetailsID, formData }, { rejectWithValue }) => {
        try {
            let dataToSend;


            if (formData instanceof FormData) {
                dataToSend = formData;

                if (!dataToSend.has('caseDetailsID')) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {

                dataToSend = formatAssetData({
                    ...formData,
                    caseDetailsID: caseDetailsID || formData.caseDetailsID
                });
            }

            const result = await createAsset(dataToSend);

            if (result.success) {
                return {
                    asset: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to create asset",
                error: error.message
            });
        }
    }
);

// =========== UPDATE ASSET ===========
export const updateAssetAction = createAsyncThunk(
    "otherDetails/updateAsset",
    async ({ id, assetData, caseDetailsID }, { rejectWithValue }) => {
        try {
            let dataToSend;

            if (assetData instanceof FormData) {
                dataToSend = assetData;
                if (!dataToSend.has('caseDetailsID') && caseDetailsID) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {
                dataToSend = formatAssetData({
                    ...assetData,
                    caseDetailsID,
                });
            }

            const result = await updateAsset(id, dataToSend);

            if (result.success) {
                return {
                    id,
                    asset: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update asset",
                error: error.message
            });
        }
    }
);

// =========== DELETE ASSET ===========
export const deleteAssetAction = createAsyncThunk(
    "otherDetails/deleteAsset",
    async ({ id, updatedBy }, { rejectWithValue }) => {
        try {
            const result = await deleteAsset(id, updatedBy);

            if (result.success) {
                return {
                    id,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to delete asset",
                error: error.message
            });
        }
    }
);

// =========== SEARCH ASSETS ===========
export const searchAssetsAction = createAsyncThunk(
    "otherDetails/searchAssets",
    async ({ caseDetailsID, searchCriteria }, { rejectWithValue }) => {
        try {
            const result = await searchAssets(caseDetailsID, searchCriteria);

            if (result.success) {
                return {
                    caseDetailsID,
                    assets: result.data,
                    count: result.count,
                    searchCriteria,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to search assets",
                error: error.message
            });
        }
    }
);

// =========== FORMAT ASSET DATA (Non-async utility action) ===========
export const formatAssetDataAction = (assetData) => ({
    type: "otherDetails/formatAssetData",
    payload: formatAssetData(assetData)
});

// =========== PARSE ATTACHMENT IDs (Non-async utility action) ===========
export const parseAttachmentIdsAction = (attachmentField) => ({
    type: "otherDetails/parseAttachmentIds",
    payload: parseAttachmentIds(attachmentField)
});

// =========== CLEAR STATE ===========
export const clearOtherDetailsStateAction = () => ({
    type: "otherDetails/clearState"
});

// =========== SET SELECTED ASSET ===========
export const setSelectedAssetAction = (asset) => ({
    type: "otherDetails/setSelectedAsset",
    payload: asset
});
